// Перетаскивание карт пальцем/мышью с зонами сброса. Горизонтальная прокрутка рядов не ломается:
// перетаскивание начинается только при заметном вертикальном движении (или любом — для руки).
import type { PlayerId } from '../types.ts';

export interface DragSpec {
  axis: 'any' | 'y';
}

interface ZoneBase {
  el: HTMLElement;
  pad: number;
  /** дополнительный запас снизу (моя панель «дотягивается» до руки) */
  padBottom?: number;
}
export type DropZone = ZoneBase & ({ kind: 'player'; pid: PlayerId } | { kind: 'table' } | { kind: 'buy' });

export interface DraggerOpts {
  /** можно ли тащить элемент; null — нельзя */
  canDrag(el: HTMLElement): DragSpec | null;
  zonesFor(el: HTMLElement): DropZone[];
  onDrop(el: HTMLElement, zone: DropZone): void;
  onStart?(el: HTMLElement): void;
  onEnd?(el: HTMLElement): void;
  onHover?(zone: DropZone | null, el: HTMLElement): void;
  /** свой «призрак» вместо копии исходника */
  ghostFor?(el: HTMLElement): HTMLElement | null;
}

export interface Dragger {
  isDragging(): boolean;
  cancel(): void;
}

interface DragState {
  el: HTMLElement;
  x0: number;
  y0: number;
  started: boolean;
  axis: 'any' | 'y';
  zones: DropZone[];
  zoneEl: HTMLElement | null;
  zone: DropZone | null;
  ghost: HTMLElement | null;
  ox: number;
  oy: number;
}

const THRESH = 10;

export function makeDragger({ canDrag, zonesFor, onDrop, onStart, onEnd, onHover, ghostFor }: DraggerOpts): Dragger {
  let st: DragState | null = null;

  function findZone(s: DragState, x: number, y: number): DropZone | null {
    for (const z of s.zones) {
      const r = z.el.getBoundingClientRect();
      if (x >= r.left - z.pad && x <= r.right + z.pad && y >= r.top - z.pad && y <= r.bottom + (z.padBottom ?? z.pad)) return z;
    }
    return null;
  }

  function start(s: DragState, e: PointerEvent): void {
    const src = s.el;
    const r = src.getBoundingClientRect();
    // «призрак» — как карта будет выглядеть на столе (ghostFor), иначе копия исходника
    const custom = ghostFor ? ghostFor(src) : null;
    const ghost = custom ?? (src.cloneNode(true) as HTMLElement);
    ghost.classList.add('dragging');
    ghost.removeAttribute('data-fx');
    const fx = document.getElementById('fx');
    if (!fx) return;
    Object.assign(ghost.style, { position: 'fixed', left: r.left + 'px', top: r.top + 'px', margin: '0', zIndex: '300', pointerEvents: 'none', transform: 'none', transition: 'none' });
    if (!custom) Object.assign(ghost.style, { width: r.width + 'px', height: r.height + 'px' });
    fx.appendChild(ghost);
    const g = ghost.getBoundingClientRect();
    s.ghost = ghost;
    // палец держит карту за верх обложки (по центру), чтобы название и текст под пальцем оставались видны
    s.ox = g.width / 2;
    s.oy = Math.min(28, g.height * 0.15);
    ghost.style.left = (e.clientX - s.ox) + 'px';
    ghost.style.top = (e.clientY - s.oy) + 'px';
    s.started = true;
    s.zones = zonesFor(src);
    src.classList.add('drag-src');
    for (const z of s.zones) z.el.classList.add('dropzone');
    document.body.classList.add('is-dragging');
    if (onStart) onStart(src);
  }

  function move(e: PointerEvent): void {
    const s = st;
    if (!s) return;
    const dx = e.clientX - s.x0;
    const dy = e.clientY - s.y0;
    if (!s.started) {
      const dist = Math.hypot(dx, dy);
      if (dist < THRESH) return;
      // из горизонтально прокручиваемого ряда — только при преимущественно вертикальном движении
      if (s.axis === 'y' && Math.abs(dy) < Math.abs(dx) * 1.2) {
        cleanup();
        return;
      }
      try {
        s.el.setPointerCapture(e.pointerId);
      } catch {
        /* не все браузеры */
      }
      start(s, e);
      if (!s.started) return;
    }
    e.preventDefault();
    const g = s.ghost;
    if (!g) return;
    const tilt = Math.max(-10, Math.min(10, (e.movementX || 0) * 0.6));
    g.style.left = (e.clientX - s.ox) + 'px';
    g.style.top = (e.clientY - s.oy) + 'px';
    g.style.transform = `scale(1.06) rotate(${tilt}deg)`;
    const z = findZone(s, e.clientX, e.clientY);
    const zEl = z ? z.el : null;
    if (zEl !== s.zoneEl) {
      if (s.zoneEl) s.zoneEl.classList.remove('dropzone-hot');
      if (zEl) zEl.classList.add('dropzone-hot');
      s.zoneEl = zEl;
      s.zone = z;
      if (onHover) onHover(z, s.el);
    }
  }

  function cleanup(): void {
    const s = st;
    if (!s) return;
    if (s.ghost) s.ghost.remove();
    s.el.classList.remove('drag-src');
    for (const z of s.zones) {
      z.el.classList.remove('dropzone');
      z.el.classList.remove('dropzone-hot');
    }
    document.body.classList.remove('is-dragging');
    st = null;
    if (onEnd) onEnd(s.el);
  }

  function up(): void {
    const s = st;
    if (!s) return;
    if (!s.started) {
      st = null;
      return;
    }
    const { zone: z, el, ghost } = s;
    if (z && ghost) {
      // клик «в зону»: клон схлопывается к зоне
      const r = z.el.getBoundingClientRect();
      const g = ghost.getBoundingClientRect();
      ghost.animate([{ transform: 'scale(1.06)', opacity: 1 }, { transform: `translate(${r.left + r.width / 2 - g.left - g.width / 2}px, ${r.top + r.height / 2 - g.top - g.height / 2}px) scale(.3)`, opacity: 0 }], { duration: 220, easing: 'ease-in', fill: 'forwards' });
      s.ghost = null;
      setTimeout(() => ghost.remove(), 240);
      cleanup();
      onDrop(el, z);
    } else if (ghost) {
      // вернуть на место
      const r = el.getBoundingClientRect();
      const g = ghost.getBoundingClientRect();
      const a = ghost.animate([{ transform: ghost.style.transform, opacity: 1 }, { transform: `translate(${r.left - g.left}px, ${r.top - g.top}px) scale(1)`, opacity: 0.6 }], { duration: 220, easing: 'ease-out', fill: 'forwards' });
      s.ghost = null;
      a.onfinish = () => ghost.remove();
      cleanup();
    } else cleanup();
  }

  document.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest<HTMLElement>('.card[data-iid]');
    if (!el) return;
    const spec = canDrag(el);
    if (!spec) return;
    st = { el, x0: e.clientX, y0: e.clientY, started: false, axis: spec.axis, zones: [], zoneEl: null, zone: null, ghost: null, ox: 0, oy: 0 };
  }, { passive: true });
  document.addEventListener('pointermove', move, { passive: false });
  document.addEventListener('pointerup', up);
  document.addEventListener('pointercancel', () => { if (st && st.started) cleanup(); else st = null; });
  return { isDragging: () => !!(st && st.started), cancel: cleanup };
}
